import { useState, useMemo, useEffect, useRef, useId } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Minus } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { useBooking, CARD_FIELDS_RESET } from '@features/booking/state';
import type { GuaranteeType } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { filterTerms, termsIdentity } from '@features/booking/utils/filterTerms';
import { computeSlotFee } from '@features/booking/utils/slotFee';
import { activeSlotMenuFor } from '@features/booking/utils/activeSlotMenu';
import { resolveTip, effectiveTipPercent } from '@features/booking/utils/gratuity';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { EarlyAccessBanner } from '@features/booking/components/EarlyAccessBanner';
import { useCommitOrAdvance } from '@features/booking/hooks/useCommitOrAdvance';
import { useSlotTerms } from '@features/booking/hooks/useSlotTerms';
import { ThreeDSChallengeOverlay } from '@features/booking/components/ThreeDSChallengeOverlay';
import { commitCtaKey } from '@features/booking/utils/commitMomentCopy';
import { verifyAttrs } from '@/shared/verify/core/contract';


function YesNoToggle({
  value,
  onChange,
  yesLabel,
  noLabel,
  hasError,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
  hasError?: boolean;
}) {
  const errorBorder = hasError ? 'border-red-400' : 'border-border';
  return (
    <div className="flex">
      <button
        type="button"
        className={`px-5 py-2 rounded-l-xl border transition-all duration-200 ${
          value === false
            ? 'bg-brand text-brand-foreground border-brand shadow-sm'
            : `bg-card text-foreground/70 ${errorBorder} hover:border-brand-light`
        }`}
        onClick={() => onChange(false)}
      >
        {noLabel}
      </button>
      <button
        type="button"
        className={`px-5 py-2 rounded-r-xl border transition-all duration-200 ${
          value === true
            ? 'bg-brand text-brand-foreground border-brand shadow-sm'
            : `bg-card text-foreground/70 ${errorBorder} hover:border-brand-light`
        }`}
        onClick={() => onChange(true)}
      >
        {yesLabel}
      </button>
    </div>
  );
}

function Counter({
  value,
  onChange,
  min = 0,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
}) {
  return (
    <div className="flex items-center space-x-2">
      <button
        type="button"
        className="w-10 h-10 rounded-xl bg-card border border-border flex items-center justify-center text-foreground/50 hover:border-brand-light transition-colors disabled:opacity-50"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
      >
        <Minus size={18} />
      </button>
      <div className="w-12 h-10 rounded-xl bg-foreground/5 border border-border flex items-center justify-center font-medium text-foreground">
        {value}
      </div>
      <button
        type="button"
        className="w-10 h-10 rounded-xl bg-card border border-border flex items-center justify-center text-foreground/50 hover:border-brand-light transition-colors"
        onClick={() => onChange(value + 1)}
      >
        <Plus size={18} />
      </button>
    </div>
  );
}

const Personalize = () => {
  const { t } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const { trackEvent } = useAnalytics();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const slotTerms = useSlotTerms();

  // T14: when Personalize is the commit host (skipConfirmStep flag on AND
  // CONFIRMATION dropped → BOOKING_FINAL is the next step), tapping Continue
  // commits the reservation instead of advancing. The hook proxies all the
  // commit-related state from useReservationCommit; the legacy branch
  // returns neutral defaults so the existing UI keeps working.
  const {
    commitOrAdvance,
    willCommit,
    isSubmitting,
    error: commitError,
    requireReload,
    isHandling3ds,
  } = useCommitOrAdvance();

  // Ref so handleContinue can call the LATEST commitOrAdvance after the
  // flushSync below. flushSync triggers a synchronous re-render which
  // refreshes this ref to point at the new callback (whose closure sees the
  // post-flush booking state). Without this, the click handler would call a
  // stale closure that reads pre-flush booking data — committing the
  // reservation with the previous personalization payload.
  const commitOrAdvanceRef = useRef(commitOrAdvance);
  commitOrAdvanceRef.current = commitOrAdvance;

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_PERSONALIZE });
  }, [trackEvent]);

  // Read restaurant config flags
  const showAllergies = !!restaurant.allowAllergies;
  const showChildren = !!restaurant.allowKidsOption;
  const showPets = !!restaurant.allowPets;
  const showAccessibility = !!restaurant.allowAccessibility;

  // Selected menu for the veggie check
  const selectedMenu = activeSlotMenuFor(booking, restaurant.publicSearchMode);
  // WL flow: no selected menu, show veggie if restaurant allows it (legacy behavior)
  const showVegetarian = booking.flowType === 'waiting_list'
    ? !!restaurant.allowVeggieMenu
    : !!restaurant.allowVeggieMenu && !!selectedMenu?.allowVeggie;

  // resolveTip: 'off' → no tip; 'on' → the experience's own rates (or the
  // venue's list if it sets none); 'follow' → the venue's switch + list.
  const resolvedTip = resolveTip(restaurant, selectedMenu);
  const serviceChargeOptions = resolvedTip.options;

  // Local state initialized from booking state.
  // hasAllergies tri-state: null = not selected yet, true = yes, false = no.
  // We can't infer "no" from `allergies.length === 0` because that also
  // matches "not yet answered". The dedicated `hasAllergiesAnswered` flag
  // (set true in handleContinue) carries the answered-ness so back-nav
  // returns restore "No" correctly (regression reported 2026-05-21).
  //
  // Backward-compat for legacy sessions: if allergies has text, the user
  // obviously answered Yes — preserve that even if the new flag is missing
  // from the stored payload. Only fall back to `null` when both signals
  // say "no evidence of answer."
  const [hasAllergies, setHasAllergies] = useState<boolean | null>(
    booking.allergies.length > 0
      ? true
      : booking.hasAllergiesAnswered ? false : null
  );
  const [allergiesText, setAllergiesText] = useState(booking.allergies);
  const [veggie, setVeggie] = useState(booking.veggie);
  const [howManyVeggie, setHowManyVeggie] = useState(booking.howManyVeggie || 1);
  const [kids, setKids] = useState(booking.kids);
  const [howManyKids, setHowManyKids] = useState(booking.howManyKids || 1);
  const [pets, setPets] = useState(booking.pets);
  const [accessibility, setAccessibility] = useState(booking.accessibility);
  const [allergiesError, setAllergiesError] = useState('');
  const [termsError, setTermsError] = useState('');

  // Gratuity selector
  const gratuityHeadingId = useId();
  const tipRadioRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [tipChoice, setSelectedTipPercent] = useState<number | null>(booking.tipChargePercent ?? null);
  const selectedTipPercent = effectiveTipPercent(resolvedTip, tipChoice) ?? null;

  // The fee the payment step will charge, with this page's unsaved answers
  // laid over booking state: special requests can turn the booking into a deposit.
  const feeBreakdown = computeSlotFee({
    ...booking,
    kids,
    howManyKids,
    veggie,
    pets,
    allergies: hasAllergies ? allergiesText : '',
    tipChargePercent: selectedTipPercent,
  }, restaurant);

  // The picker shows when the fee above charges a tip and there is more than one rate to choose.
  // WL flow: no gratuity (legacy: mustChargeTip = false for WL)
  const showGratuity = booking.flowType !== 'waiting_list' &&
    !!feeBreakdown?.chargesGratuity &&
    serviceChargeOptions.length > 1;

  // Slot-level terms filtered by selected time and area.
  // WL flow has no selected slot/time — slot terms don't apply (legacy behavior).
  const applicableTerms = useMemo(() => {
    if (booking.flowType === 'waiting_list') return [];
    const areaId = booking.selectedArea ? Number(booking.selectedArea.id) : null;
    return filterTerms(slotTerms, booking.selectedTime, areaId);
  }, [booking.flowType, slotTerms, booking.selectedTime, booking.selectedArea]);

  const [slotTermsAccepted, setSlotTermsAccepted] = useState<boolean[]>(
    () => new Array(applicableTerms.length).fill(false)
  );
  // Continue is not disabled while terms are unaccepted, so the inline error
  // is the only feedback: the first unaccepted term is brought into view and
  // focused, otherwise a long page makes Continue look like a no-op.
  const termAcceptRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Terms are re-fetched while this page is mounted (language switch, or a
  // refetch once the slots go stale), so acceptance is kept only while the
  // terms still refer to the same text: a translation preserves the identity,
  // a different set of terms has to be accepted again.
  const termsKey = termsIdentity(applicableTerms);
  const acceptedTermsKeyRef = useRef(termsKey);
  useEffect(() => {
    if (acceptedTermsKeyRef.current !== termsKey) {
      acceptedTermsKeyRef.current = termsKey;
      setSlotTermsAccepted(new Array(applicableTerms.length).fill(false));
    }
  }, [termsKey, applicableTerms.length]);

  const handleContinue = () => {
    // Validate allergies selection is made
    if (showAllergies && hasAllergies === null) {
      setAllergiesError(t('PERSONALIZE_ALLERGIES_REQUIRED'));
      return;
    }

    // Validate allergies text if toggled on
    if (hasAllergies && allergiesText.trim().length < 5) {
      setAllergiesError(t('PERSONALIZE_ALLERGIES_MIN_LENGTH'));
      return;
    }

    // Validate slot-level terms
    if (applicableTerms.length > 0 && !slotTermsAccepted.every(Boolean)) {
      setTermsError(t('CHECK_TERMS_CONDITIONS'));
      const firstUnaccepted = termAcceptRefs.current[slotTermsAccepted.findIndex((accepted) => !accepted)];
      firstUnaccepted?.scrollIntoView?.({ block: 'center' });
      firstUnaccepted?.focus();
      return;
    }

    // Fee computed at render from current personalization + gratuity. WL flow has
    // no selected slot — its guarantee comes from the WL rules API and must
    // be preserved here. For standard flow we always trust computeSlotFee,
    // including its null result, so removing a special-request trigger after a
    // card was tokenized clears the stale guarantee instead of preserving it.
    const isWlFlow = booking.flowType === 'waiting_list';
    let feeDeposit = isWlFlow ? booking.feeDeposit : 0;
    let feeGratuity = isWlFlow ? booking.feeGratuity : 0;
    let feeGratuityPercent = isWlFlow ? booking.feeGratuityPercent : 0;
    let guaranteeType: GuaranteeType = isWlFlow ? booking.guaranteeType : 'traditional';

    if (feeBreakdown) {
      feeDeposit = feeBreakdown.total;
      feeGratuity = feeBreakdown.gratuity;
      feeGratuityPercent = feeBreakdown.gratuityPercent;
      if (feeBreakdown.guaranteeType) {
        guaranteeType = feeBreakdown.guaranteeType;
      } else {
        guaranteeType = 'traditional';
      }
    }

    // Clear any tokenized card when the payment signature changes — the user
    // accepted a specific type AND amount; reusing the token under different
    // values would charge a different total than what was acknowledged.
    const paymentSignatureChanged =
      booking.guaranteeType !== guaranteeType ||
      booking.feeDeposit !== feeDeposit ||
      booking.feeGratuity !== feeGratuity;

    // flushSync so the next nav/commit call sees the updated cardToken=null
    // and re-includes the GUARANTEE step instead of routing straight to
    // BOOKING_FINAL. Equally critical for the commit branch: the commit
    // payload is built from the latest booking state on the next render
    // (see commitOrAdvanceRef above).
    flushSync(() => {
      booking.updateBooking({
        allergies: hasAllergies ? allergiesText.trim() : '',
        // Mark the toggle as answered — reaching handleContinue means the
        // user did click Yes or No (validation above rejects null). This
        // lets the init logic on return distinguish "user said No" from
        // "user hasn't visited the page yet."
        hasAllergiesAnswered: true,
        veggie,
        howManyVeggie: veggie ? howManyVeggie : 0,
        kids,
        howManyKids: kids ? howManyKids : 0,
        pets,
        accessibility,
        tipChargePercent: selectedTipPercent,
        feeDeposit,
        feeGratuity,
        feeGratuityPercent,
        guaranteeType,
        ...(paymentSignatureChanged
          ? {
              ...CARD_FIELDS_RESET,
              // The resolved processor could differ under the new signature
              // (e.g. dolarized MP→Stripe shift) — unlike the failed-commit
              // reset in useReservationCommit, which keeps it.
              paymentProcessor: null,
            }
          : {}),
      });
    });

    // Post-flushSync: ref now points at the callback whose closure sees the
    // latest booking state. The hook decides commit vs goToNext based on
    // whether the next step in the active set is BOOKING_FINAL.
    //
    // Why Personalize does NOT capture intent (unlike Stripe/MP payment
    // forms): the only Personalize-side mutation that flips willCommit is
    // the payment-signature-change branch clearing `cardToken`, which
    // RE-INTRODUCES GUARANTEE into the active set — exactly the case where
    // we WANT the post-flushSync live state to take over and route to
    // /payment instead of committing. Capturing intent here would route
    // to BOOKING_FINAL with a stale cardToken and skip card re-entry.
    // Payment forms have the inverse case (cardToken being SET filters
    // GUARANTEE out), so they must capture — see CapturedCommitIntent
    // JSDoc in useCommitOrAdvance.ts for the full mechanism.
    void commitOrAdvanceRef.current();
  };

  return (
    <BookingPageLayout
      title={t('NEW_MODAL_PERSONALIZE_EXPERIENCE')}
      subtitle={t('PERSONALIZE_SUBTITLE')}
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <EarlyAccessBanner />
      <div
        className="w-full sm:px-6 space-y-8"
        {...verifyAttrs("PersonalizePage", {
          willCommit,
          hasTipPicker: showGratuity,
          hostStep: "personalize",
          // T30 D1: hasFee is always false on Personalize — the fee is
          // displayed and charged on the Guarantee step, not here. Even
          // when Personalize is the commit host (post-flag-flip), the
          // CTA copy is the unadorned "Reservar"; the user already saw
          // the fee in the breakdown earlier.
          hasFee: false,
          hasError: !!commitError,
          isSubmitting,
          requireReload,
          // Resolved i18n key the live button is about to render.
          // Pinned by the verify spec so a regression in commitCtaKey
          // (e.g. CONFIRMATION_CONFIRM_BOOKING reappearing) fails CI.
          ctaKey: requireReload
            ? "PAYMENT_3DS_RELOAD_TO_RETRY"
            : willCommit
              ? commitCtaKey({
                  hostStep: "personalize",
                  hasFee: false,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                })
              : "NEW_MODAL_CONTINUE",
          // Unconditional helper output — always computed regardless of
          // which branch the live button took. Lets the verify matrix
          // pin the commit-branch CTA contract even on tenants where
          // the willCommit branch isn't reachable in the framework's
          // current seed propagation (the issue review #229 flagged on
          // PersonalizePage.verify.ts).
          helperCtaKey: commitCtaKey({
            hostStep: "personalize",
            hasFee: false,
            isSubmitting,
            hasError: !!commitError,
            isWaitingList: booking.flowType === 'waiting_list',
          }),
        })}
      >
        {/* Allergies */}
        {showAllergies && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className={`text-lg font-medium ${allergiesError ? 'text-red-500' : 'text-foreground'}`}>
                {t('STRING_MENU_ALLERGIES_TITLE')}
              </p>
              <YesNoToggle
                value={hasAllergies}
                onChange={(v) => {
                  setHasAllergies(v);
                  setAllergiesError('');
                }}
                yesLabel={t('STRING_YES')}
                noLabel={t('STRING_NO')}
                hasError={!!allergiesError && hasAllergies === null}
              />
            </div>
            {allergiesError && hasAllergies === null && (
              <p role="alert" className="text-red-500 text-sm">{allergiesError}</p>
            )}
            {hasAllergies && (
              <div>
                <textarea
                  className={`w-full rounded-xl border p-3 text-foreground focus:border-brand focus:outline-none resize-none ${
                    allergiesError ? 'border-red-400' : 'border-border translucent:border-surface-border'
                  }`}
                  rows={3}
                  placeholder={t('NEW_MODAL_ALLERGIES_PLACEHOLDER')}
                  value={allergiesText}
                  onChange={(e) => {
                    setAllergiesText(e.target.value);
                    if (e.target.value.trim().length >= 5) setAllergiesError('');
                  }}
                />
                {allergiesError && (
                  <p role="alert" className="text-red-500 text-sm mt-1">{allergiesError}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Vegetarian */}
        {showVegetarian && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-lg font-medium text-foreground">{t('NEW_MODAL_GUESTS_VEGETARIAN')}</p>
              <YesNoToggle
                value={veggie}
                onChange={setVeggie}
                yesLabel={t('STRING_YES')}
                noLabel={t('STRING_NO')}
              />
            </div>
            {veggie && (
              <div className="flex justify-between items-center">
                <p className="text-foreground/70">{t('STRING_MENU_VEGETAL_OPTION_DESCRIPTION')}</p>
                <Counter value={howManyVeggie} onChange={setHowManyVeggie} min={1} />
              </div>
            )}
          </div>
        )}

        {/* Children */}
        {showChildren && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-lg font-medium text-foreground">{t('NEW_MODAL_GUESTS_CHILDREN')}</p>
              <YesNoToggle
                value={kids}
                onChange={setKids}
                yesLabel={t('STRING_YES')}
                noLabel={t('STRING_NO')}
              />
            </div>
            {kids && (
              <div className="flex justify-between items-center">
                <p className="text-foreground/70">{t('STRING_MENU_VEGETAL_OPTION_DESCRIPTION')}</p>
                <Counter value={howManyKids} onChange={setHowManyKids} min={1} />
              </div>
            )}
          </div>
        )}

        {/* Pets */}
        {showPets && (
          <div className="flex justify-between items-center">
            <p className="text-lg font-medium text-foreground">{t('NEW_MODAL_PET')}</p>
            <YesNoToggle
              value={pets}
              onChange={setPets}
              yesLabel={t('STRING_YES')}
              noLabel={t('STRING_NO')}
            />
          </div>
        )}

        {/* Accessibility */}
        {showAccessibility && (
          <div className="flex justify-between items-center">
            <p className="text-lg font-medium text-foreground">{t('NEW_MODAL_ACCESSIBILITY_TABLE')}</p>
            <YesNoToggle
              value={accessibility}
              onChange={setAccessibility}
              yesLabel={t('STRING_YES')}
              noLabel={t('STRING_NO')}
            />
          </div>
        )}

        {/* Gratuity Selector */}
        {showGratuity && (
          <div className="space-y-3">
            <p id={gratuityHeadingId} className="text-lg font-medium text-foreground">{t('NEW_MODAL_SERVICE_GRATUITY')}</p>
            <div role="radiogroup" aria-labelledby={gratuityHeadingId} className="flex flex-wrap gap-2">
              {serviceChargeOptions.map((pct, i) => (
                <button
                  key={pct}
                  ref={(el) => { tipRadioRefs.current[i] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={selectedTipPercent === pct}
                  tabIndex={selectedTipPercent === pct ? 0 : -1}
                  onKeyDown={(e) => {
                    if (e.altKey || e.metaKey || e.ctrlKey) return;
                    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
                      : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
                    if (!step) return;
                    e.preventDefault();
                    const next = (i + step + serviceChargeOptions.length) % serviceChargeOptions.length;
                    setSelectedTipPercent(serviceChargeOptions[next]);
                    tipRadioRefs.current[next]?.focus();
                  }}
                  className={`px-5 py-2 rounded-xl border text-sm transition-all duration-200 ${
                    selectedTipPercent === pct
                      ? 'bg-brand text-brand-foreground border-brand shadow-sm'
                      : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                  }`}
                  onClick={() => setSelectedTipPercent(pct)}
                >
                  {pct === 0 ? t('NEW_MODAL_NO_TIP') : `${pct}%`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Slot-level Terms & Conditions */}
        {applicableTerms.length > 0 && (
          <div className="space-y-3">
            {applicableTerms.map((term, idx) => (
              <div key={idx} className={`rounded-xl border p-4 transition-colors ${
                termsError && !slotTermsAccepted[idx] ? 'border-red-400 bg-red-50' : 'border-border'
              }`}>
                <div className="flex flex-col gap-3">
                  <div
                    className="text-sm text-foreground [&_a]:text-brand [&_a]:underline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(term.text || '') }}
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      ref={(el) => {
                        termAcceptRefs.current[idx] = el;
                      }}
                      onClick={() => {
                        const next = [...slotTermsAccepted];
                        next[idx] = !next[idx];
                        setSlotTermsAccepted(next);
                        if (next.every(Boolean)) setTermsError('');
                      }}
                      className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                        slotTermsAccepted[idx]
                          ? 'bg-brand text-brand-foreground'
                          : 'bg-foreground/5 text-foreground/60 hover:bg-foreground/10'
                      }`}
                    >
                      {slotTermsAccepted[idx]
                        ? t('STRING_ACCEPTED')
                        : t('STRING_ACCEPT')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {termsError && (
              <p role="alert" className="text-red-500 text-sm">{termsError}</p>
            )}
          </div>
        )}
      </div>

      {/* Commit error (shown only when Personalize is the commit host — the
          legacy advance branch never sets this). Mirrors ConfirmationPage's
          inline-error pattern so the user sees the same copy regardless of
          which page hosts the commit. */}
      {commitError && (
        <div
          data-testid="personalize-commit-error"
          className="bg-red-50 text-red-700 rounded-xl p-4 text-sm w-full sm:px-6 mt-6"
        >
          {commitError}
        </div>
      )}

      <div className="w-full sm:px-6 mt-12">
        <Button
          data-testid="continue-btn"
          {...verifyAttrs("PersonalizeSubmit", { action: "confirm-booking" })}
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
          onClick={handleContinue}
          disabled={isSubmitting || requireReload}
        >
          {requireReload
            ? tErrors('PAYMENT_3DS_RELOAD_TO_RETRY')
            : !willCommit
              ? // Legacy advance branch — moves user to /confirmation, where
                // the real commit happens. Existing label preserved.
                t('NEW_MODAL_CONTINUE')
              : // T30 commit-moment copy: state-aware CTA key resolved
                // through `commitCtaKey`. Covers in-flight, retry-after-
                // error, and the steady-state Personalize CTA in one
                // helper so all four commit hosts agree on the contract.
                t(commitCtaKey({
                  hostStep: 'personalize',
                  hasFee: false,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }))}
        </Button>
      </div>

      {/* T30 D2: full-screen overlay while the bank verifies the 3DS challenge.
          Only flips open when willCommit && a 3DS challenge is mid-flight;
          inert otherwise. */}
      <ThreeDSChallengeOverlay open={isHandling3ds} />
    </BookingPageLayout>
  );
};

export default Personalize;
